import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import JSLayout from "./js_layout";

const savedCss = `
  .sj-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:1rem; }
  .sj-card { background:#fff; border:1px solid rgba(var(--color-text-rgb),0.07); border-radius:4px; padding:1.25rem 1.5rem; transition:all 0.2s; cursor:pointer; }
  .sj-card:hover { border-color:rgba(var(--color-primary-rgb),0.3); box-shadow:0 2px 14px rgba(var(--color-primary-rgb),0.07); transform:translateY(-1px); }
  .sj-card-top { display:flex; align-items:flex-start; gap:0.75rem; margin-bottom:0.85rem; }
  .sj-logo { width:42px; height:42px; border-radius:4px; background:var(--color-bg-subtle); border:1px solid rgba(var(--color-text-rgb),0.07); display:flex; align-items:center; justify-content:center; font-size:1.1rem; flex-shrink:0; }
  .sj-title { font-size:0.92rem; font-weight:500; color:var(--color-text); margin-bottom:2px; }
  .sj-company { font-size:0.78rem; color:#aaa; font-weight:300; }
  .sj-tags { display:flex; gap:5px; flex-wrap:wrap; margin-bottom:0.85rem; }
  .sj-footer { display:flex; align-items:center; justify-content:space-between; }
  .sj-salary { font-size:0.85rem; font-weight:500; color:var(--color-text); }
  .sj-actions { display:flex; gap:0.5rem; }
  .sj-btn { font-size:0.65rem; padding:5px 12px; border-radius:2px; cursor:pointer; font-family:'DM Sans',sans-serif; transition:all 0.2s; }
  .sj-btn.apply { background:var(--color-text); color:var(--color-bg-subtle); border:1px solid var(--color-text); }
  .sj-btn.apply:hover { background:var(--color-primary); border-color:var(--color-primary); color:var(--color-text); }
  .sj-btn.remove { background:transparent; color:#bbb; border:1px solid rgba(var(--color-text-rgb),0.1); }
  .sj-btn.remove:hover { border-color:#e74c3c; color:#e74c3c; }
  .sj-saved-date { font-size:0.68rem; color:#bbb; margin-bottom:0.5rem; }
  .sj-loading, .sj-empty { text-align:center; padding:3rem 1rem; color:#aaa; font-size:0.88rem; width:100%; grid-column: 1 / -1; }
  .sj-empty-icon { font-size: 2rem; margin-bottom: 0.5rem; }
  @media(max-width:768px){ .sj-grid{grid-template-columns:1fr;} }
`;

export function SavedJobs() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Inject CSS Stylesheet Rule Layer
  useEffect(() => {
    if (typeof document !== "undefined" && !document.getElementById("bl-sj-css")) {
      const s = document.createElement("style");
      s.id = "bl-sj-css";
      s.textContent = savedCss;
      document.head.appendChild(s);
    }
  }, []);

  // Fetch Saved Jobs from the DB on component mount
  useEffect(() => {
    const fetchSavedJobs = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");
      try {
        const res = await fetch(API_ENDPOINTS.SAVEDJOBS, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setItems(data);
        }
      } catch (err) {
        console.error("Failed to load saved jobs from backend API database:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSavedJobs();
  }, []);

  // Remove/Unsave Handler mapping back to backend sync endpoints
  const removeSavedJob = async (e, id) => {
    e.stopPropagation(); // Stop parent layout card redirect navigate event bubble
    
    const token = localStorage.getItem("token");
    
    // Optimistic UI state removal update for immediate feeling interaction loop
    const previousItems = [...items];
    setItems(prev => prev.filter(item => item.id !== id));

    try {
      // Reusing your save toggle action which typically handles both save/unsave on backend
      const res = await fetch(`${API_ENDPOINTS.JOBS}/${id}/save`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        // Rollback state if server fails to authenticate or respond
        setItems(previousItems);
      }
    } catch (err) {
      console.error("Error un-saving job listing from database:", err);
      setItems(previousItems); // Rollback state safety mesh
    }
  };

  const getJobIcon = (category) => {
    const icons = {
      "Hair Styling": "✂️",
      "Nail Technology": "💅",
      "Esthetics / Skincare": "🧖",
      "Makeup Artistry": "💄",
      "Barbering": "🪒",
      "Lash & Brow": "👁️",
      "Beauty Education": "🏫",
      "Salon Management": "💼",
    };
    return icons[category] || "✨";
  };

  return (
    <JSLayout title="Saved" titleEm="Jobs">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem" }}>
        <div style={{ fontSize: "0.82rem", color: "#aaa" }}>
          <strong style={{ color: "var(--color-text)" }}>{loading ? "..." : items.length}</strong> saved jobs
        </div>
      </div>

      <div className="sj-grid">
        {loading ? (
          <div className="sj-loading">Retrieving your saved listings...</div>
        ) : items.length === 0 ? (
          <div className="sj-empty">
            <div className="sj-empty-icon">♡</div>
            <div>You haven't saved any job listings yet.</div>
          </div>
        ) : (
          items.map(job => (
            <div key={job.id} className="sj-card" onClick={() => navigate(`/jobs/${job.id}`)}>
              <div className="sj-card-top">
                <div className="sj-logo">{getJobIcon(job.spec || job.specialisation || job.category)}</div>
                <div>
                  <div className="sj-title">{job.title}</div>
                  <div className="sj-company">{job.company || job.salonName}</div>
                </div>
              </div>
              
              {job.savedDate && (
                <div className="sj-saved-date">Saved {new Date(job.savedDate).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })}</div>
              )}
              
              <div className="sj-tags">
                <span className="bl-tag bl-tag-loc">📍 {job.location || job.city}</span>
                <span className="bl-tag bl-tag-type">{job.type || job.jobType}</span>
              </div>
              
              <div className="sj-footer">
                <div className="sj-salary">
                  {job.salary ? (typeof job.salary === 'number' ? `KSh ${job.salary.toLocaleString()}/mo` : job.salary) : "Salary Undisclosed"}
                </div>
                <div className="sj-actions">
                  {job.hasApplied ? (
                    <span className="bl-tag bl-tag-applied" style={{ fontSize: '0.65rem', padding: '5px 12px', background: '#f5f5f5', color: '#aaa', borderRadius: '2px' }}>
                      ✓ Applied
                    </span>
                  ) : (
                    <button 
                      className="sj-btn apply" 
                      onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${job.id}`); }}
                    >
                      Apply
                    </button>
                  )}

                  <button 
                    className="sj-btn remove" 
                    onClick={(e) => removeSavedJob(e, job.id)}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </JSLayout>
  );
}